/**
 * @description 폰 카메라의 실제 능력(플래시/줌/전면·후면)을 다루는 곳
 *
 * Flashphoner SDK 의 Stream 에는 로컬 트랙을 꺼내는 공개 API 가 없다(flashphoner-core.js 의 stream.* 목록 참고).
 * 그래서 SDK 가 #localDisplay 안에 만들어 둔 video 엘리먼트에서 트랙을 직접 가져온다.
 * SDK 를 올릴 때 깨질 수 있는 부분이라 이 파일 밖으로 새어나가지 않게 한다.
 *
 * 실기기 확인 결과(2026-09-17, iPhone 18.7 / Android Chrome / 삼성 인터넷):
 *  - 플래시·줌은 기기가 아니라 "지금 열려 있는 카메라"마다 지원 여부가 다르다.
 *    (안드로이드 2종은 기본 후면 카메라에 토치가 없었고, iPhone 은 후면 전부 있었다)
 *  - 카메라가 하나라도 열려 있으면 다른 카메라를 열 수 없다.
 *    iOS 는 기존 트랙이 죽고, 안드로이드는 NotReadableError 가 난다.
 *  - getUserMedia 를 부를 때마다 권한 팝업이 뜨는 환경이 있다(iOS Safari, 삼성 인터넷).
 *    삼성 인터넷은 Permissions API 가 granted 라고 답하면서도 팝업을 띄워 사전 판별이 불가능했다.
 *    => 이 파일은 getUserMedia 를 절대 부르지 않는다. 능력은 이미 송출 중인 트랙에서 읽고,
 *       전/후면 deviceId 는 enumerateDevices 라벨로 찾는다.
 *  - 줌 범위는 카메라마다 다르고(0.5~10 / 1~10), iOS 는 step 을 주지 않는다.
 */

import { constants } from '@flashphoner/websdk';

export type CameraFacing = 'front' | 'rear';
export type ZoomDirection = 'in' | 'out';

export interface ZoomRange {
  min: number;
  max: number;
}

export interface CameraCaps {
  torch: boolean;
  zoom: ZoomRange | null;
  facing: CameraFacing;
}

export interface CameraSet {
  rear: string | null;
  front: string | null;
  canSwitch: boolean;
}

const LOCAL_DISPLAY_SELECTOR = '#localDisplay video';
/** 라벨은 브라우저·언어마다 다르다. 한국어와 영어를 함께 본다. */
const FRONT_LABEL = /front|user|전면|셀카/i;
const REAR_LABEL = /back|rear|environment|후면/i;
/** 광각·망원 같은 보조 렌즈보다 기본 카메라를 고른다. */
const SPECIALTY_LABEL = /ultra|wide|tele|dual|triple|와이드|망원|듀얼|트리플/i;
/** 누를 때마다 배율을 곱한다. iOS 는 step 을 주지 않고, 안드로이드 step 0.1 은 너무 잘다. */
const ZOOM_FACTOR = 1.5;

export const getLocalVideoTrack = (): MediaStreamTrack | null => {
  const video = document.querySelector(LOCAL_DISPLAY_SELECTOR) as HTMLVideoElement | null;
  const stream = video && (video.srcObject as MediaStream | null);
  if (!stream) {
    return null;
  }
  const tracks = stream.getVideoTracks();
  return tracks.length > 0 ? tracks[0] : null;
};

const capabilitiesOf = (track: MediaStreamTrack): any => {
  try {
    return track.getCapabilities ? track.getCapabilities() : {};
  } catch (error) {
    return {};
  }
};

const settingsOf = (track: MediaStreamTrack): any => {
  try {
    return track.getSettings ? track.getSettings() : {};
  } catch (error) {
    return {};
  }
};

/** iOS 는 torch: true, 안드로이드는 키 자체가 없거나 [false, true] 형태로 온다. */
const hasTorch = (caps: any): boolean => {
  if (caps.torch === true) {
    return true;
  }
  return Array.isArray(caps.torch) && caps.torch.indexOf(true) !== -1;
};

const zoomRangeOf = (caps: any): ZoomRange | null => {
  if (!caps.zoom || typeof caps.zoom.max !== 'number' || typeof caps.zoom.min !== 'number') {
    return null;
  }
  if (caps.zoom.max <= caps.zoom.min) {
    return null;
  }
  return { min: caps.zoom.min, max: caps.zoom.max };
};

export const readCameraCaps = (track: MediaStreamTrack): CameraCaps => {
  const caps = capabilitiesOf(track);
  const settings = settingsOf(track);
  const facingValue = settings.facingMode || (Array.isArray(caps.facingMode) ? caps.facingMode[0] : caps.facingMode);
  return {
    torch: hasTorch(caps),
    zoom: zoomRangeOf(caps),
    facing: facingValue === 'user' ? 'front' : 'rear',
  };
};

export const readCurrentZoom = (track: MediaStreamTrack): number | null => {
  const zoom = settingsOf(track).zoom;
  return typeof zoom === 'number' ? zoom : null;
};

export const readDeviceId = (track: MediaStreamTrack): string | null => {
  const deviceId = settingsOf(track).deviceId;
  return typeof deviceId === 'string' && deviceId ? deviceId : null;
};

/** 지원한다고 보고돼도 실제 적용은 실패할 수 있다. 성공 여부를 돌려준다. */
export const setTorch = async (track: MediaStreamTrack, on: boolean): Promise<boolean> => {
  try {
    await track.applyConstraints({ advanced: [{ torch: on }] } as any);
    return true;
  } catch (error) {
    console.log('플래시 적용 실패 ', error);
    return false;
  }
};

export const isSwitchUnsupportedError = (error: unknown): boolean => error === constants.ERROR_INFO.CAN_NOT_SWITCH_CAM;

export const describeCameraSwitchError = (error: unknown): string => {
  if (isSwitchUnsupportedError(error)) {
    return '이 기기/브라우저에서는 카메라 전환을 지원하지 않습니다.';
  }
  const name = (error as any)?.name;
  switch (name) {
    case 'NotReadableError':
    case 'AbortError':
      return '카메라를 열 수 없습니다. 다른 앱이 사용 중이거나 기기가 아직 해제되지 않았습니다.';
    case 'OverconstrainedError':
      return '전환할 카메라가 요청한 해상도를 지원하지 않습니다.';
    case 'NotAllowedError':
      return '카메라 권한이 거부되었습니다.';
    case 'NotFoundError':
      return '전환할 카메라를 찾지 못했습니다.';
    default:
      return (error as any)?.message || String(error) || '카메라 전환에 실패했습니다.';
  }
};

export const setZoom = async (track: MediaStreamTrack, value: number): Promise<boolean> => {
  try {
    await track.applyConstraints({ advanced: [{ zoom: value }] } as any);
    return true;
  } catch (error) {
    console.log('줌 적용 실패 ', error);
    return false;
  }
};

/** capability 가 채워졌다고 볼 수 있는 키. 크롬은 torch/zoom 을 photo capability 와 함께 뒤늦게 채운다. */
const hasSettledCaps = (caps: any): boolean => caps.torch !== undefined || caps.zoom !== undefined;

/**
 * torch/zoom capability 가 채워질 때까지(최대 timeoutMs) 기다렸다가 읽는다.
 * 실기기 확인(2026-09-21, Android 크롬): 트랙이 살아난 직후에는 두 키가 없다가 1~2초 안에 함께 나타난다.
 */
export const waitForCameraCaps = async (track: MediaStreamTrack, timeoutMs = 2000): Promise<CameraCaps> => {
  const started = Date.now();
  while (!hasSettledCaps(capabilitiesOf(track)) && Date.now() - started < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
  return readCameraCaps(track);
};

/** 라벨의 카메라 번호("camera 0, facing back", "camera2 2, …"). 없으면 큰 값으로 뒤로 보낸다. */
const cameraIndexOf = (label: string): number => {
  const matched = label.match(/camera2?\s*(\d+)/i);
  return matched ? parseInt(matched[1], 10) : Number.MAX_SAFE_INTEGER;
};

/**
 * 후면 카메라 목록을 "플래시가 있을 가능성이 높은 순"으로 돌려준다.
 * 실기기 확인(2026-09-21): facingMode:'environment' 로 열린 후면(camera 2)에는 torch 가 없고 camera 0 에만 있었다.
 * 안드로이드는 거의 항상 카메라 0 이 플래시 달린 기본 후면이므로 번호 오름차순으로 정렬한다.
 * 라벨은 권한이 허용된 뒤에만 채워지므로 송출 시작 후에 불러야 한다.
 */
export const listRearCameras = async (): Promise<MediaDeviceInfo[]> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === 'videoinput' && device.deviceId && REAR_LABEL.test(device.label))
      .sort((a, b) => cameraIndexOf(a.label) - cameraIndexOf(b.label));
  } catch (error) {
    console.log('카메라 목록 조회 실패 ', error);
    return [];
  }
};

export const nextZoom = (current: number | null, range: ZoomRange, direction: ZoomDirection): number => {
  const base = current !== null && current > 0 ? current : range.min;
  const raw = direction === 'in' ? base * ZOOM_FACTOR : base / ZOOM_FACTOR;
  const clamped = Math.min(range.max, Math.max(range.min, raw));
  return Math.round(clamped * 100) / 100;
};

/**
 * 카메라를 열지 않고 라벨만으로 전/후면을 찾는다.
 * getUserMedia 를 부르지 않으므로 권한 팝업이 뜨지 않는다.
 * 라벨은 권한이 허용된 뒤에야 채워지므로 송출이 시작된 다음에 호출해야 한다.
 */
export const resolveCamerasByLabel = async (currentDeviceId: string | null): Promise<CameraSet> => {
  const result: CameraSet = { rear: currentDeviceId, front: null, canSwitch: false };
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((device) => device.kind === 'videoinput' && device.deviceId);
    const pick = (pattern: RegExp): MediaDeviceInfo | undefined => {
      const matched = cameras.filter((device) => pattern.test(device.label));
      // 보조 렌즈가 아닌 기본 카메라를 우선한다 (iPhone 은 전면도 울트라와이드가 따로 잡힌다)
      return matched.find((device) => !SPECIALTY_LABEL.test(device.label)) || matched[0];
    };
    const front = pick(FRONT_LABEL);
    if (front) {
      result.front = front.deviceId;
    }
    if (result.rear === null) {
      const rear = pick(REAR_LABEL);
      if (rear) {
        result.rear = rear.deviceId;
      }
    }
  } catch (error) {
    console.log('카메라 목록 조회 실패 ', error);
  }
  result.canSwitch = result.rear !== null && result.front !== null && result.rear !== result.front;
  return result;
};
